import { forwardRef, useCallback, type ChangeEvent, type InputHTMLAttributes } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export function slugify(text: string): string {
	return text
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "") // Remove diacritics
		.replace(/[^a-z0-9\s-]/g, "") // Remove special characters
		.replace(/\s+/g, "-") // Replace spaces with hyphens
		.replace(/-+/g, "-") // Replace multiple hyphens with single
		.replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
}

export interface SlugInputProps
	extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "type"> {
	value?: string;
	onChange?: (value: string) => void;
}

export const SlugInput = forwardRef<HTMLInputElement, SlugInputProps>(
	function SlugInput({ className, value, onChange, ...props }, ref) {
		const handleChange = useCallback(
			function handleChangeCallback(event: ChangeEvent<HTMLInputElement>) {
				const rawValue = event.target.value;
				const slugValue = slugify(rawValue);
				onChange?.(slugValue);
			},
			[onChange],
		);

		return (
			<Input
				ref={ref}
				type="text"
				className={cn(className)}
				value={value ?? ""}
				onChange={handleChange}
				{...props}
			/>
		);
	},
);
